import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  LineController,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";
import pattern from "patternomaly";

import { formatNumber } from "../../utils/formatter";
import Loading from "../Loading";
import InfoModal from "../InfoModal";
import { useSelector } from "react-redux";

//================================================================
// 1) Register ChartJS + annotation plugin ==================================
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  annotationPlugin
);

const IntensityTab = ({ activeTab, showBtn = true }) => {
  const { fromYear } = useSelector((state) => state.selectedYearItem);
  const intensityData = useSelector((state) => state.pdfData);
  const legislativeScenarioName = intensityData?.legislativeScenarioName;
  const pricingScenarioName = intensityData?.priceScenarioName;
  const imoRewardPrice = intensityData?.myIMOMTMIntensityResults?.imoRewardPrice;
  const imoSUSalePrice = intensityData?.myIMOMTMIntensityResults?.imoSUSalePrice;
  const euSUSalePrice = intensityData?.myFMResults?.euSUSalePrice;

  const metaReady =
    legislativeScenarioName &&
    pricingScenarioName != null;



  const [checked, setChecked] = useState(true);
  const handleChange = (e) => {
    setChecked(e.target.checked);
  };

  // State for chart
  const [label, setLabel] = useState([]);
  const [dataset, setDataset] = useState([]);

  // Keep track of intensity & credits
  const [reportedFm, setReportedFm] = useState([]);
  const [unaidedFm, setUnaidedFm] = useState([]);
  const [externalPayment, setExternalPayment] = useState([]);
  const [barBase_mtCO2eq, setBarBase_mtCO2eq] = useState([]);

  // FEUM only----------------------------------
  // quantities
  const [mt_EU_intraPool, setMt_EU_intraPool] = useState([]);
  const [mt_EU_RU, setMt_EU_RU] = useState([]);
  const [mt_EU_soldExt, setMt_EU_soldExt] = useState([]);
  // bars
  const [bar_EU_intraPool, setBar_EU_intraPool] = useState([]);
  const [bar_EU_RU, setBar_EU_RU] = useState([]);
  const [bar_EU_soldExt, setBar_EU_soldExt] = useState([]);

  // IMO only----------------------------------
  // quantities
  const [RU_tier_1_mtCO2eq, setRU_tier_1_mtCO2eq] = useState([]);
  const [RU_tier_2_mtCO2eq, setRU_tier_2_mtCO2eq] = useState([]);
  const [SU_generated_mtCO2eq, setSU_generated_mtCO2eq] = useState([]);
  const [SU_consumed_mtCO2eq, setSU_consumed_mtCO2eq] = useState([]);
  const [SU_sold_mtCO2eq, setSU_sold_mtCO2eq] = useState([]);
  // bars
  const [barSUgenerated, setBarSUgenerated] = useState([]);
  const [barSUconsumed, setBarSUconsumed] = useState([]);
  const [barSUsold, setBarSUsold] = useState([]);
  const [barRU1, setBarRU1] = useState([]);
  const [barRU2, setBarRU2] = useState([]);

  const [chartKey, setChartKey] = useState(0);
  const [show, setShow] = useState(false);
  const IntensityContainerRef = useRef(null);
  const euFmChartRef = useRef(null);

  useEffect(() => {
    /* --------------------------------------------------------------------
       1) Select the right result set (FEUM vs IMO)
    -------------------------------------------------------------------- */
    const resultsData = checked
      ? intensityData?.myIMOMTMIntensityResults?.myResultsIMO
      : intensityData?.myFMResults?.myResultsFM;

    if (!resultsData) return;

    /* --------------------------------------------------------------------
       2) Build & sort the label list
    -------------------------------------------------------------------- */
    const labels = Object.keys(resultsData);

    // For FEUM sorting logic (intra-pool), we may not even need it for IMO, but we can keep it consistent:
    const sortingArray = labels.map((name) => ({
      name,
      poolTrade_as_intensityDelta:
        resultsData[name]?.poolTrade_as_intensityDelta || 0
    }));

    // Sort ascending by poolTrade_as_intensityDelta, tie-break by reportedIntensity
    sortingArray.sort((a, b) => {
      if (a.poolTrade_as_intensityDelta !== b.poolTrade_as_intensityDelta) {
        return a.poolTrade_as_intensityDelta - b.poolTrade_as_intensityDelta;
      }
      return (
        (resultsData[a.name]?.reportedIntensity_gCo2eq_MJ || 0) -
        (resultsData[b.name]?.reportedIntensity_gCo2eq_MJ || 0)
      );
    });

    const sortedLabels = sortingArray.map(item => item.name);

    /* --------------------------------------------------------------------
       3) Helper and local arrays (everything is synchronous here)
    -------------------------------------------------------------------- */
    const build = (prop, scale = 1) =>
      sortedLabels.map(name => (resultsData[name]?.[prop] ?? 0) / scale);

    /* ---------- shared ---------- */
    const reportedFmArr = build("reportedIntensity_gCo2eq_MJ");
    const unaidedFmArr = build("unaidedIntensity_gCo2eq_MJ");
    const externalPaymentArr = build("external_intensity_penalty_USD");
    const barBase_mtCO2eqArr = build("barBase");

    /* ---------- FEUM ---------- */
    const mt_EU_intraPoolArr = build("poolTrade_gCO2eq_basis365", 1e6);
    const mt_EU_RUArr = build("externalRUs_gCO2eq_basis365", 1e6);
    const mt_EU_soldExtArr = build("soldExternally_gCO2eq_basis365", 1e6);

    const bar_EU_intraPoolArr = build("poolTrade_as_intensityDelta");
    const bar_EU_RUArr = build("externalCredit_as_intensityDelta");
    const bar_EU_soldExtArr = build("externalSale_as_intensityDelta");

    /* ---------- IMO ---------- */
    const ru_tier_1_mtCO2eqArr = build("ru_tier_1_mtCO2eq");
    const ru_tier_2_mtCO2eqArr = build("ru_tier_2_mtCO2eq");
    const su_generated_mtCO2eqArr = build("su_generated_mtCO2eq");
    const su_consumed_mtCO2eqArr = build("su_consumed_mtCO2eq");
    const su_sold_mtCO2eqArr = build("su_sold_externally_mtCO2eq");

    const barRU1Arr = build("barRU1");
    const barRU2Arr = build("barRU2");
    const barSUgeneratedArr = build("barSUgen");
    const barSUconsumedArr = build("barSUcons");
    const barSUsoldArr = build("barSUsold");

    /* --------------------------------------------------------------------
       4) Build datasets FROM THOSE LOCAL ARRAYS
    -------------------------------------------------------------------- */
    const BAR_THICKNESS = 22;
    let datasetResult = [];

    if (!checked) {
      /* =========================== FEUM mode =========================== */
      datasetResult = [
        {
          label: "Base",
          data: sortedLabels.map((_, i) =>
            Math.min(reportedFmArr[i], unaidedFmArr[i])
          ),
          backgroundColor: "gray",
          barThickness: BAR_THICKNESS
        },
        {
          label: "FEUM Remedial Units",
          data: bar_EU_RUArr,
          backgroundColor: pattern.draw("diagonal", "#333333"),
          barThickness: BAR_THICKNESS
        },
        { // TODO: The F variable is still two-sided, maybe fix this
          label: "FEUM SU from pool",
          data: bar_EU_intraPoolArr.map(v => (v < 0 ? -v : 0)),
          backgroundColor: "#FF6666",
          barThickness: BAR_THICKNESS
        },
        {
          label: "FEUM SU to pool",
          data: bar_EU_intraPoolArr.map(v => (v > 0 ? v : 0)),
          backgroundColor: "#3CB371",
          barThickness: BAR_THICKNESS
        },
        {
          label: "FEUM SU sold externally",
          data: bar_EU_soldExtArr,
          backgroundColor: "#6495ED",
          barThickness: BAR_THICKNESS
        }
      ];
    } else {
      /* =========================== IMO MTM mode ======================== */
      datasetResult = [
        {
          label: "Base",
          data: barBase_mtCO2eqArr,
          backgroundColor: "gray",
          barThickness: BAR_THICKNESS
        },
        {
          label: "Remedial Units Tier 1",
          data: barRU1Arr,
          backgroundColor: pattern.draw("diagonal", "#000080"),
          barThickness: BAR_THICKNESS
        },
        {
          label: "Remedial Units Tier 2",
          data: barRU2Arr,
          backgroundColor: pattern.draw("diagonal-right-left", "#333333"),
          barThickness: BAR_THICKNESS
        },
        {
          label: "IMO SU from pool",
          data: barSUconsumedArr,
          backgroundColor: "#FF6666",
          barThickness: BAR_THICKNESS
        },
        {
          label: "IMO SU to pool",
          data: barSUgeneratedArr,
          backgroundColor: "#3CB371",
          barThickness: BAR_THICKNESS
        },
        {
          label: "IMO SU sold externally",
          data: barSUsoldArr,
          backgroundColor: "#6495ED",
          barThickness: BAR_THICKNESS
        }
      ];
    }

    /* --------------------------------------------------------------------
       5) Commit chart data and *then* update the individual series state
    -------------------------------------------------------------------- */
    setLabel(sortedLabels);
    setDataset(datasetResult);

    /* ---------- push every array to its original state setter ---------- */
    setReportedFm(reportedFmArr);
    setUnaidedFm(unaidedFmArr);
    setExternalPayment(externalPaymentArr);
    setBarBase_mtCO2eq(barBase_mtCO2eqArr);

    setMt_EU_intraPool(mt_EU_intraPoolArr);
    setMt_EU_RU(mt_EU_RUArr);
    setMt_EU_soldExt(mt_EU_soldExtArr);

    setBar_EU_intraPool(bar_EU_intraPoolArr);
    setBar_EU_RU(bar_EU_RUArr);
    setBar_EU_soldExt(bar_EU_soldExtArr);

    setRU_tier_1_mtCO2eq(ru_tier_1_mtCO2eqArr);
    setRU_tier_2_mtCO2eq(ru_tier_2_mtCO2eqArr);
    setSU_generated_mtCO2eq(su_generated_mtCO2eqArr);
    setSU_consumed_mtCO2eq(su_consumed_mtCO2eqArr);
    setSU_sold_mtCO2eq(su_sold_mtCO2eqArr);

    setBarRU1(barRU1Arr);
    setBarRU2(barRU2Arr);
    setBarSUgenerated(barSUgeneratedArr);
    setBarSUconsumed(barSUconsumedArr);
    setBarSUsold(barSUsoldArr);
  }, [intensityData, checked]);


  // Prepare data for the chart
  const dataEUFM = {
    labels: label,
    datasets: dataset
  };

  // Force chart re-render if tab changes
  useEffect(() => {
    if (activeTab === 3) {
      setChartKey((prev) => prev + 1);
    }
  }, [activeTab]);

  //================================================================
  // 5) Intensity Annotations ================================== 

  const annotations = {};

  // 5.1. Reference line
  const referenceFEUM = 91.16;
  const referenceIMO = 93.30;
  const referenceLevelGFILine = checked ? referenceIMO : referenceFEUM;
  const labelRefLevel = `Reference ${referenceLevelGFILine.toFixed(2)}`;

  annotations.referenceLevel = {
    type: 'line',
    yMin: referenceLevelGFILine,
    yMax: referenceLevelGFILine,
    borderColor: 'red',
    borderDash: [3, 1],
    borderWidth: 2,
    label: {
      display: true,
      content: labelRefLevel,
      position: 'start', // or 'center', 'end'
    }
  };

  // 5.2. Annotation for one or two intensity limits
  const feumMax = intensityData?.myFMResults?.maxGHG_intensitygCO2_MJ ?? 0;
  const baseGFI_limit = intensityData?.myIMOMTMIntensityResults?.base_GFI_gCO2_MJ ?? 0;
  const directGFI_limit = intensityData?.myIMOMTMIntensityResults?.direct_GFI_gCO2_MJ ?? 0;
  const currentBaseGFILine = checked ? baseGFI_limit : feumMax;
  const currentDirectGFILine = checked ? directGFI_limit : 0;
  const labelBaseCompliance = checked ? `Base Target GFI: ${currentBaseGFILine.toFixed(2)}` : `EUFM max Intensity: ${currentBaseGFILine.toFixed(2)} `;
  const labelDirectCompliance = checked ? `Direct Compliance GFI: ${currentDirectGFILine.toFixed(2)}` : "";

  if (currentBaseGFILine > currentDirectGFILine) {
    // Add base compliance only when it is different from direct compliance
    annotations.maxGHGLine1 = {
      type: 'line',
      yMin: currentBaseGFILine,
      yMax: currentBaseGFILine,
      borderColor: 'red',
      borderDash: [2, 2],
      borderWidth: 2,
      label: {
        display: true,
        content: labelBaseCompliance,
        position: 'start', // or 'center', 'end'
        // Optionally configure font styles, background, etc.
      }
    };
  };

  // Conditionally add the second line. Only applies for IMO
  if ((currentDirectGFILine) > 0) {
    annotations.maxGHGLine2 = {
      type: 'line',
      yMin: currentDirectGFILine,
      yMax: currentDirectGFILine,
      borderColor: 'red',
      borderDash: [3, 1],
      borderWidth: 2,
      label: {
        display: true,
        content: labelDirectCompliance,
        position: 'start', // or 'center', 'end'
        // Optionally configure font styles, background, etc.
      }
    };
  }
  // 5.3. reward level annotation. Applies for IMO only
  const rewardLevelIMO = intensityData?.myIMOMTMIntensityResults?.reward_threshold_GFI_gCO2_MJ ?? 0;
  const currentRewardLevel = checked ? rewardLevelIMO : 0;
  const labelRewardLevel = `Reward Threshold: ${currentRewardLevel.toFixed(2)}`;

  if (currentRewardLevel > 0) {
    annotations.rewardLevel = {
      type: 'line',
      yMin: currentRewardLevel,
      yMax: currentRewardLevel,
      borderColor: 'green',
      borderDash: [3, 1],
      borderWidth: 2,
      label: {
        display: true,
        content: labelRewardLevel,
        position: 'start', // or 'center', 'end'
        // Optionally configure font styles, background, etc.
      }
    };
  }

  //================================================================
  // 6) Build tooltip logic ==================================
  const tooltipCallbacks = {
    label: (context) => {
      // Only show the tooltip for the “Base” dataset
      if (context.datasetIndex !== 0) return "";

      const i = context.dataIndex;
      if (!reportedFm[i]) return ["No emissions within EU scope."];

      const at = reportedFm[i].toFixed(2);
      const un = unaidedFm[i].toFixed(2);

      if (!checked) {
        // FEUM tooltip 
        let RU = mt_EU_RU[i].toFixed(2);
        let credits = mt_EU_intraPool[i].toFixed(2);
        let SUsold = mt_EU_soldExt[i].toFixed(2);

        return [
          `Reported Intensity: ${at} [gCO2eq/MJ]`,
          `Unaided Intensity:  ${un} [gCO2eq/MJ]`,
          RU > 0 ? `FEUM Remedial Units : ${formatNumber(RU)} [mtCO2]` : '',
          credits > 0 ? `FEUM SU to Pool: ${formatNumber(credits)} [mtCO2]` : '',
          credits < 0 ? `FEUM SU from Pool: ${formatNumber(credits)} [mtCO2]` : '',
          SUsold > 0 ? `FEUM SU sold externally: ${formatNumber(SUsold)} [mtCO2]` : ''
        ].filter(Boolean);

      } else {
        // IMO tooltip 
        let RU1 = RU_tier_1_mtCO2eq[i].toFixed(2);
        let RU2 = RU_tier_2_mtCO2eq[i].toFixed(2);
        let SUgen = SU_generated_mtCO2eq[i].toFixed(2);
        let SUcons = SU_consumed_mtCO2eq[i].toFixed(2);
        let SUsold = SU_sold_mtCO2eq[i].toFixed(2);

        return [
          `Reported Intensity: ${at} [gCO2eq/MJ]`,
          `Unaided Intensity:  ${un} [gCO2eq/MJ]`,
          RU1 > 0 ? `IMO RU Tier 1: ${formatNumber(RU1)} [mtCO2]` : '',
          RU2 > 0 ? `IMO RU Tier 2: ${formatNumber(RU2)} [mtCO2]` : '',
          SUgen > 0 ? `IMO SU to Pool: ${formatNumber(SUgen)} [mtCO2]` : '',
          SUcons > 0 ? `IMO SU from Pool: ${formatNumber(SUcons)} [mtCO2]` : '',
          SUsold > 0 ? `IMO SU sold externally: ${formatNumber(SUsold)} [mtCO2]` : ''
        ].filter(Boolean);
      }
    },
    title: (context) => context[0].label
  };

  //================================================================
  // 7) Chart options ==================================
  const options = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        stacked: true,
        grid: { display: true },
        min: 0,
        max: 100,
        title: {
          display: true,
          text: "[gCO2eq/MJ]",
          color: "#000000",
        },
        ticks: {
          callback: (value) => formatNumber(value),
          font: { size: 15, family: "Cabin, sans-serif" }
        }
      },
      x: {
        stacked: true,
        grid: { display: true },
        ticks: {
          display: false,
          minRotation: 90,
          color: "#000000",
          padding: 1,
          font: { size: 14, weight: "bold", family: "Cabin, sans-serif" }
        }
      }
    },
    layout: { padding: { top: 75 } },
    plugins: {
      datalabels: {
        align: "end",
        anchor: "start",
        color: "#000000",
        borderRadius: 30,
        padding: { left: 10, right: 10, top: 8, bottom: 8 },
        rotation: -90,
        font: () => ({
          size: 15,
          weight: "bold",
          family: "Cabin, sans-serif"
        }),
        display: (context) => context.dataset.label === "Base",
        formatter: (value, context) => label[context.dataIndex]
      },
      legend: {
        // For this particular chart, this will create chaos if the RUt2 fall into RUt1 area
        onClick: () => { },
        position: "bottom",
        labels: {
          padding: 40,
          color: "#212529",
          font: {
            size: 15,
            family: "Cabin, sans-serif"
          },
          generateLabels: (chart) => {
            // Start with the default labels:
            let labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

            // 1) Always remove the first item in the legend:
            labels = labels.slice(1);

            // 2) If in IMO mode (checked == true), also remove pooling & external references:
            if (checked) {
              labels = labels.filter(
                (item) =>
                  !item.text.includes("IntraPool")
              );
            }

            return labels;
          }
        }
      },
      tooltip: {
        position: "nearest",
        displayColors: false,
        mode: "index",
        intersect: false,
        callbacks: tooltipCallbacks
      },
      annotation: {
        annotations: annotations
      }
    }
  };

  // Scroll logic for modal
  useEffect(() => {
    if (show) {
      IntensityContainerRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [show]);

  const handleClose = () => {
    setShow(false);
    euFmChartRef?.current?.scrollIntoView({ behavior: "auto" });
  };

  //================================================================
  // 8) HTML ==================================
  return (
    <div ref={IntensityContainerRef}>
      <div className="tab-content-title d-flex justify-content-between flex-wrap align-items-start">
        {/* Left side: Title + Subtitles stacked */}
        <div>
          <h2>
            {checked ? "IMO-MTM Intensity" : "FEUM Intensity"}{" "}
            <span className="fs-4">{fromYear?.item}</span>
          </h2>
          {/* Subtitles, smaller font */}

          {metaReady && (
            <div className="fs-6">
              {/* legislative + pricing lines */}
              {[
                ["Legislative Scenario:", legislativeScenarioName],
                ["Pricing Scenario:", pricingScenarioName],
              ].map(([label, value]) => (
                <span key={label} className="d-block">
                  <span className="fw-normal">{label}&nbsp;</span>
                  <span className="fw-bold">{value}</span>
                </span>
              ))}

              {/* price line (IMO or EU) */}
              <span className="d-block">
                {checked ? (
                  <>
                    <span className="fw-normal">IMO SU sale:&nbsp;</span>
                    <span className="fw-bold">
                      {Number(imoSUSalePrice).toFixed(2)} &nbsp;[USD/mtCO₂eq]
                    </span>
                    <span className="ms-3 fw-normal">IMO reward:&nbsp;</span>
                    <span className="fw-bold">
                      {Number(imoRewardPrice).toFixed(2)}&nbsp;[USD/mtCO₂eq]
                    </span>
                  </>
                ) : (
                  <>
                    <span className="fw-normal">FEUM SU sale price:&nbsp;</span>
                    <span className="fw-bold">
                      {Number(euSUSalePrice).toFixed(2)}&nbsp;[USD/mtCO₂eq]
                    </span>
                  </>
                )}
                
              </span>
            </div>
          )}

        </div>

        {/* Right side: Toggle + Button */}

        {showBtn && (
          <div className="d-flex flex-wrap align-items-center gap-3 mt-2 mt-lg-0">
            {/* Toggle */}
            <div className="form-check form-switch ft-1rem d-flex align-items-center gap-2 justify-content-start">
              <span className="text-nowrap">FEUM</span>
              <input
                className="form-check-input cursor-pointer no-dim-toggle"
                type="checkbox"
                id="custom-switch"
                checked={checked}
                onChange={handleChange}
                style={{ marginLeft: "5px" }} // Adjust margin if necessary
              />
              <span className="text-nowrap">IMO-MTM</span>
            </div>

            {/* Notes and Sources button */}
            <div>
              <button className="btn btn-primary" onClick={() => setShow(!show)}>
                Notes and Sources
              </button>
            </div>
          </div>
        )}
      </div>


      <div className="fleet-chart-container ">
        {/* If no data is loaded, show Loading */}
        {(!intensityData || Object.keys(intensityData).length === 0) &&
          label.length === 0 &&
          dataset.length === 0 && (
            <div className="d-flex align-items-center justify-content-center h-100">
              <Loading loading />
            </div>
          )}

        {/* If data is present, show the chart */}
        {intensityData &&
          Object.keys(intensityData).length > 0 &&
          label.length > 0 &&
          dataset.length > 0 && (
            <Bar data={dataEUFM} options={options} key={chartKey} />
          )}
      </div>

      <InfoModal
        ref={euFmChartRef}
        show={show}
        handleClose={handleClose}
        data={
          checked
            ? intensityData?.myIMOMTMIntensityResults?.comments
            : intensityData?.myFMResults?.comments
        }
      />
    </div>
  );
};

export default IntensityTab;
